import React, { useState } from "react";
import { Link } from "gatsby";
import Navbar from "components/Navbar/navbar";
import Footer from "components/Footer/footer";
import DarkTheme from "layouts/Dark";
import ParticalsOverlay from "components/Particals-Overlay/ParticalsOverlay";
import SEO from "components/seo/seo";
import LoadingScreen from "components/Loading-Screen";
import ContactFromDate from "data/sections/form-info.json";

const policyEffectiveDate = "April 1, 2026";
const privacySections = [
  {
    title: "Introduction And Scope",
    points: [
      "This Privacy Policy explains how Evolvan collects, uses, stores, shares, and protects personal information when you visit our website, contact us, request services, or otherwise interact with us.",
      "This policy applies to website visitors, prospective clients, clients, business contacts, and other individuals who share information with us in a professional or service-related context.",
      "This policy does not apply to third-party websites, apps, or platforms that have their own privacy notices and are not controlled by Evolvan.",
      "We aim to handle personal information responsibly and in line with applicable privacy laws, including GDPR and CCPA/CPRA standards where they apply.",
      "Our website is intended for general business use, and we do not knowingly collect personal information from children through this website.",
    ],
  },
  {
    title: "What Data We Collect",
    points: [
      "Contact information such as your name, email address, phone number, company name, and any information you submit through website forms, emails, calls, or project enquiries.",
      "Profile information you choose to share with us, such as your job title, business background, project goals, portfolio links, or other professional details relevant to our services.",
      "Communication data, including messages, attachments, feedback, meeting notes, proposal details, and other information exchanged with our team.",
      "Usage and technical data such as IP address, browser type, device information, pages viewed, referring links, date and time of visits, and general interaction data.",
      "Cookie and similar technology data used to remember preferences, understand site usage, improve performance, and support analytics or site management tools.",
    ],
  },
  {
    title: "How We Use Collected Data",
    points: [
      "To respond to enquiries, schedule consultations, prepare proposals, manage projects, and provide the services you request.",
      "To operate, maintain, secure, troubleshoot, and improve our website, content, and service delivery.",
      "To understand how visitors use the website, measure performance, and improve user experience, design decisions, and important pages.",
      "To send service-related information, project updates, administrative notices, and marketing communications where permitted by law.",
      "To comply with legal obligations, maintain business records, resolve disputes, enforce our terms, and protect our business, users, and website.",
    ],
  },
  {
    title: "Legal Bases For Processing",
    points: [
      "Where GDPR applies, we may process personal data because it is necessary to take steps at your request, enter into a contract with you, or perform our contractual obligations.",
      "We may process personal information to comply with legal obligations such as tax, accounting, regulatory, or lawful request requirements.",
      "We may process data for our legitimate interests, including securing the website, improving services, managing client relationships, and preventing misuse, provided those interests are not overridden by your rights.",
      "Where required, we rely on your consent for specific processing activities, and you may withdraw that consent at any time.",
    ],
  },
  {
    title: "Cookies And Similar Technologies",
    points: [
      "We use cookies and similar technologies to support website functionality, remember preferences, understand traffic patterns, and improve the website experience.",
      "Analytics or tag management tools may collect information such as page views, traffic sources, browser or device details, and engagement with site content.",
      "Some cookies may be set by third-party tools that help us measure performance or manage website operations.",
      "You can manage or disable cookies through your browser or device settings, although doing so may affect how some parts of the website function.",
      "Any non-essential cookies are intended to be used subject to applicable legal requirements.",
    ],
  },
  {
    title: "How We Store, Retain, And Protect Data",
    points: [
      "We use reasonable administrative, technical, and organizational safeguards to help protect personal information against unauthorized access, misuse, loss, alteration, or disclosure.",
      "Access to personal information is limited to team members, contractors, and service providers who need the information for legitimate business purposes.",
      "We retain personal information only for as long as reasonably necessary to respond to enquiries, deliver services, maintain records, resolve disputes, and comply with legal, accounting, tax, or security obligations.",
      "When information is no longer required, we aim to delete, anonymize, or securely dispose of it in an appropriate manner.",
      "No website, internet transmission, or storage system can be guaranteed to be completely secure, but we take privacy and security seriously.",
    ],
  },
  {
    title: "Data Sharing With Third Parties",
    points: [
      "We do not sell your personal information and do not exchange it for monetary value.",
      "We may share personal information with trusted service providers that help us operate the website or deliver services, such as hosting, analytics, communications, development, accounting, security, or business support providers.",
      "We may disclose information when required by law, to respond to lawful requests, to protect rights or safety, to investigate misuse, or in connection with a merger, acquisition, financing, or business reorganization.",
      "Some service providers may process information in India or other countries where they operate. Where required, we take reasonable steps to use appropriate safeguards for those transfers.",
    ],
  },
  {
    title: "Your Rights And Choices",
    points: [
      "Depending on your location and applicable law, you may have the right to request access to the personal information we hold about you.",
      "You may request correction of inaccurate or incomplete information, deletion of information we are not required to keep, or a copy of certain information in a portable format where applicable.",
      "You may also object to or request restriction of certain processing, withdraw consent where processing is based on consent, and opt out of marketing communications at any time.",
      "California residents may request information about the categories of personal information collected, sources, business purposes, categories of third parties receiving information, and may request deletion or correction subject to legal exceptions.",
      "We will not discriminate against you for exercising privacy rights that apply to you. We may need to verify your identity before completing certain requests.",
    ],
  },
  {
    title: "Changes To This Privacy Policy",
    points: [
      "We may update this Privacy Policy from time to time to reflect legal, technical, operational, or business changes.",
      "When we make changes, we will post the updated version on this page and revise the effective date shown here.",
      "If a change is material, we may also provide additional notice where appropriate. We encourage you to review this page periodically.",
    ],
  },
];

const PrivacyPolicy = () => {
  const navbarRef = React.useRef(null);
  const logoRef = React.useRef(null);
  const fixedHeader = React.useRef(null);
  const mainContentRef = React.useRef(null);
  const [theme, setTheme] = useState("Dark");

  React.useEffect(() => {
    const navbar = navbarRef.current;

    if (window.pageYOffset > 300) {
      navbar.classList.add("nav-scroll");
    } else {
      navbar.classList.remove("nav-scroll");
    }

    window.addEventListener("scroll", () => {
      if (window.pageYOffset > 300) {
        navbar.classList.add("nav-scroll");
      } else {
        navbar.classList.remove("nav-scroll");
      }
    });

    const storage = localStorage.getItem("theme");

    if (storage && storage != "") {
      const bodyEl = document.querySelector("body");
      bodyEl.classList.add("themeL");
      setTheme(storage);
    }

    setTimeout(() => {
      if (fixedHeader.current && mainContentRef.current) {
        const slidHeight = fixedHeader.current.offsetHeight;
        mainContentRef.current.style.marginTop = slidHeight + "px";
      }
    }, 1000);
  }, [fixedHeader, navbarRef]);

  const themeModeChange = (themeMode) => {
    const bodyEl = document.querySelector("body");

    if (themeMode == "Dark") {
      setTheme("themeL");
      bodyEl.classList.add("themeL");
      localStorage.setItem("theme", "themeL");
    } else {
      setTheme("Dark");
      bodyEl.classList.remove("themeL");
      localStorage.removeItem("theme");
    }
  };

  return (
    <DarkTheme>
      <LoadingScreen />
      <Navbar
        nr={navbarRef}
        lr={logoRef}
        theme={theme}
        themeModeChange={themeModeChange}
      />

      <header
        ref={fixedHeader}
        className="works-header fixed-slider hfixd valign sub-bg"
      >
        <ParticalsOverlay />
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10 static">
              <div className="capt mt-50">
                <div className="parlx text-center">
                  <h1 className="color-font">Privacy Policy</h1>
                  <p>
                    Learn how Evolvan collects, uses, shares, and protects
                    personal information when you use our website or contact our
                    team.
                  </p>
                  <Link
                    to="/contact"
                    className="butn bord curve mt-30 banner-cta"
                    onClick={() => {
                      checkPaceClass();
                    }}
                  >
                    Contact our team
                  </Link>
                </div>

                <div className="bactxt custom-font valign">
                  <span className="full-width banner-bg-large-text">
                    Privacy
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div ref={mainContentRef} className="main-content">
        <section className="intro-section section-padding">
          <div className="container">
            <div className="row justify-content-center py-5">
              <div className="col-lg-10">
                <div className="row align-items-start">
                  <div className="col-lg-4 md-mb50">
                    <div className="cont">
                      <h3 className="wow color-font">
                        Your privacy matters
                      </h3>
                      <p className="mt-20">
                        This policy is written in plain English so you can
                        clearly understand what information we collect, why we
                        collect it, how we protect it, and what rights you may
                        have.
                      </p>
                    </div>
                  </div>

                  <div className="col-lg-8">
                    <div className="cont">
                      <p>
                        This Privacy Policy applies to people who visit the
                        Evolvan website, submit forms, communicate with us by
                        email or phone, request our services, or otherwise
                        interact with our business online. It is designed to be
                        transparent and to reflect privacy expectations under
                        laws such as the GDPR and the CCPA/CPRA where those laws
                        apply.
                      </p>
                      <div className="row mt-40">
                        <div className="col-md-4 md-mb30">
                          <div className="item">
                            <h6 className="color-font mb-15">Effective date</h6>
                            <p>{policyEffectiveDate}</p>
                          </div>
                        </div>
                        <div className="col-md-4 md-mb30">
                          <div className="item">
                            <h6 className="color-font mb-15">
                              Privacy contact
                            </h6>
                            <p>
                              <a href="mailto:info@evolvan.com">
                                info@evolvan.com
                              </a>
                            </p>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="item">
                            <h6 className="color-font mb-15">Coverage</h6>
                            <p>Website visitors, enquiries, and clients</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="services box lficon section-padding position-re sub-bg pb-50">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="sec-head text-center mb-80">
                  <h3 className="wow color-font">
                    How We Handle Information
                  </h3>
                </div>
              </div>
            </div>

            <div className="row">
              {privacySections.map((section, index) => (
                <div className="col-lg-6 wow fadeInUp" key={section.title}>
                  <div
                    className="item-box no-curve mb-30"
                    style={{ height: "calc(100% - 30px)" }}
                  >
                    <div className="cont">
                      <h6 className="color-font mb-20">
                        {`${String(index + 1).padStart(2, "0")}.`}{" "}
                        {section.title}
                      </h6>
                      <ul className="rest">
                        {section.points.map((point) => (
                          <li key={point} className="mb-15">
                            <p>{point}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="row">
                  <div className="col-lg-6 md-mb50">
                    <div
                      className="item-box no-curve mb-30"
                      style={{ height: "calc(100% - 30px)" }}
                    >
                      <div className="cont">
                        <h4 className="color-font mb-30">
                          LinkedIn And External Platforms
                        </h4>
                        <p className="mb-20">
                          LinkedIn is referenced on this website only as an
                          external social media profile link.
                        </p>
                        <ul className="rest">
                          <li className="mb-15">
                            <p>
                              LinkedIn appears on this website only as a social
                              profile link, including in the footer and on the
                              contact page.
                            </p>
                          </li>
                          <li className="mb-15">
                            <p>
                              Clicking a LinkedIn link will take you away from
                              our website and redirect you to LinkedIn&apos;s
                              external website.
                            </p>
                          </li>
                          <li className="mb-15">
                            <p>
                              We do not collect, access, or store your LinkedIn
                              account information, login credentials, or profile
                              data through this website.
                            </p>
                          </li>
                          <li className="mb-15">
                            <p>
                              We do not use LinkedIn OAuth, LinkedIn Login, or
                              any LinkedIn API integration on this website.
                            </p>
                          </li>
                          <li className="mb-15">
                            <p>
                              If you choose to visit LinkedIn, your use of that
                              platform is subject to LinkedIn&apos;s own Privacy
                              Policy:{" "}
                              <a
                                href="https://www.linkedin.com/legal/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                https://www.linkedin.com/legal/privacy-policy
                              </a>
                            </p>
                          </li>
                          <li className="mb-15">
                            <p>
                              We are not responsible for the privacy, security,
                              or data handling practices of LinkedIn or other
                              third-party platforms we link to.
                            </p>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div
                      className="item-box no-curve mb-30"
                      style={{ height: "calc(100% - 30px)" }}
                    >
                      <div className="cont">
                        <h4 className="color-font mb-30">
                          Contact Information
                        </h4>
                        <p className="mb-20">
                          You can contact us with privacy questions or requests
                          to access, update, correct, or delete your personal
                          information where applicable.
                        </p>
                        <ul className="rest">
                          <li className="mb-15">
                            <p>
                              Email:{" "}
                              <a href={`mailto:${ContactFromDate.email}`}>
                                {ContactFromDate.email}
                              </a>
                            </p>
                          </li>
                          <li className="mb-15">
                            <p>
                              Phone:{" "}
                              <a href="tel:+918360801493">
                                {ContactFromDate.phone}
                              </a>
                            </p>
                          </li>
                          <li className="mb-15">
                            <p>
                              Address: {ContactFromDate.location.first}{" "}
                              {ContactFromDate.location.second}
                            </p>
                          </li>
                          <li className="mb-15">
                            <p>
                              To make a privacy request, please contact us with
                              enough detail for us to understand and verify your
                              request. We may ask for reasonable information to
                              confirm your identity before acting on the
                              request.
                            </p>
                          </li>
                          <li className="mb-15">
                            <p>
                              We will respond within a reasonable timeframe and,
                              where required by law, within the time period set
                              by applicable privacy regulations.
                            </p>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-80 pt-30 text-center">
                  <h5 className="color-font mb-20">
                    Questions about your privacy?
                  </h5>
                  <p className="mb-30">
                    If you would like to exercise your privacy rights or need
                    clarification about how Evolvan handles personal
                    information, our team is here to help.
                  </p>
                  <Link
                    to="/contact"
                    className="butn bord curve"
                    onClick={() => {
                      checkPaceClass();
                    }}
                  >
                    Reach out to us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-80">
          <Footer />
        </div>
      </div>
    </DarkTheme>
  );
};

export const Head = () => {
  const liveUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <SEO
      canonical={liveUrl}
      title="Privacy Policy | Evolvan"
      description="Read Evolvan's Privacy Policy to understand what personal information we collect, how we use it, how we protect it, and what privacy rights you may have."
    />
  );
};

export default PrivacyPolicy;
